/**
 * LgpdKpiStrip — Métricas KPI do Centro de Direitos do Titular.
 *
 * 4 cards: pendentes | aguardando confirmação | bloqueadas LegalHold | prazo crítico ≤7d.
 * Design canônico: Brief 5 B1 (b5-a4-kpi / b5-b1-kpis).
 * P15: números sem CPF/nome — minimização de dados em KPI público.
 */

import type { LgpdRequestWireItem, LegalHoldWireItem } from './types';

interface LgpdKpiStripProps {
  requests: LgpdRequestWireItem[];
  legalHolds: LegalHoldWireItem[];
}

export function LgpdKpiStrip({ requests, legalHolds }: LgpdKpiStripProps): JSX.Element {
  const OPEN_STATUSES = ['received', 'identity_validation', 'identity_validated', 'in_processing'];

  const pending = requests.filter((r) => OPEN_STATUSES.includes(r.status)).length;

  const awaitingConfirmation = requests.filter((r) => r.status === 'pending_information').length;

  const activeHolds = legalHolds.filter((h) => h.status === 'active').length;

  const criticalDeadline = requests.filter(
    (r) => !r.isOverdue && r.daysUntilDeadline <= 7 && OPEN_STATUSES.includes(r.status),
  ).length;

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: 12,
        marginBottom: 20,
      }}
    >
      <KpiCard label="Em andamento" value={pending} hint="aguardando ação DPO" variant="default" />
      <KpiCard
        label="Pendente de informação"
        value={awaitingConfirmation}
        hint="aguardando dados do titular"
        variant={awaitingConfirmation > 0 ? 'warn' : 'default'}
      />
      <KpiCard
        label="Bloqueadas por LegalHold"
        value={activeHolds}
        hint={activeHolds > 0 ? 'aguardando liberação judicial' : 'nenhuma ativa'}
        variant={activeHolds > 0 ? 'danger' : 'default'}
      />
      <KpiCard
        label="Prazo crítico (≤7d)"
        value={criticalDeadline}
        hint="art. 19 LGPD · 15 dias"
        variant={criticalDeadline > 0 ? 'warn' : 'default'}
      />
    </div>
  );
}

// ─── KpiCard ──────────────────────────────────────────────────────────────────

interface KpiCardProps {
  label: string;
  value: number;
  hint: string;
  variant: 'default' | 'warn' | 'danger';
}

function KpiCard({ label, value, hint, variant }: KpiCardProps): JSX.Element {
  const valueColor = variant === 'danger' ? '#ef4444' : variant === 'warn' ? '#f59e0b' : '#111827';

  return (
    <div
      style={{
        background: '#fff',
        border: '1px solid #e5e7eb',
        borderRadius: 8,
        padding: '12px 16px',
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
      }}
    >
      <span
        style={{
          fontSize: 11,
          fontWeight: 600,
          color: '#6b7280',
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
        }}
      >
        {label}
      </span>
      <span
        style={{ fontSize: 28, fontWeight: 700, color: valueColor, lineHeight: 1 }}
        data-testid="kpi-value"
      >
        {value}
      </span>
      <span style={{ fontSize: 11, color: '#9ca3af' }}>{hint}</span>
    </div>
  );
}
